import { useCallback, useLayoutEffect, useMemo, useReducer } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useEditorRemixCommands } from '@atlassian/malleable-ui-components/editor-remix/commands';
import type { EditorRemixCommandHandlers } from '@atlassian/malleable-ui-components/editor-remix/commands';
import { useEditorRemixLifecycle } from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';
import { useIsAnyRemixing } from '@atlassian/malleable-ui-components/state/machine';
import {
	DEFAULT_REMIX_ERROR_MESSAGE,
	REMIX_REMOTE_ABORT_REASON,
} from '@atlassian/malleable-ui-components/types';

import { setIsAnyRemixing } from '../editor-commands/set-is-any-remixing';
import { resetRemixThinkingPresentation } from '../editor-commands/set-remix-thinking-complete';
import { setRemixThinkingLifecycle } from '../editor-commands/set-remix-thinking-lifecycle';
import type { MalleableUiPlugin } from '../malleableUiPluginType';
import { useEditorRemixLifecycleMirror } from './editorRemixLifecycleMirror';
import {
	consumeEditorRemixMutationInvalidation,
	getEditorRemixLifecyclePresentation,
	getEditorRemixUxHandles,
	markEditorRemixLifecyclePhase,
	subscribeToEditorRemixLifecyclePresentation,
} from './utils/editor-remix-callback-registry';
import type { RemixThinkingLifecycleProjection } from './utils/editor-remix-lifecycle-projection';

/** Projects machine lifecycle and terminal effects outside the conditionally mounted Remix UI. */
export const EditorRemixEffectHost = ({
	api,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
}): null => {
	const { remixId, phase, terminalPhase } = useEditorRemixLifecycle();
	const isAnyRemixing = useIsAnyRemixing();
	const [, refreshPresentation] = useReducer((count: number) => count + 1, 0);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useLayoutEffect(
		() => subscribeToEditorRemixLifecyclePresentation(api, refreshPresentation),
		[api, remixId],
	);
	const presentation = getEditorRemixLifecyclePresentation(api);

	const dispatchProjection = useCallback(
		(projection: RemixThinkingLifecycleProjection) => {
			api?.core.actions.execute(setRemixThinkingLifecycle(projection));
		},
		[api],
	);
	const resetTerminalPresentation = useCallback(
		(commandRemixId: string) => {
			// A stale terminal command must not reset a replacement presentation.
			if (commandRemixId !== remixId) {
				return;
			}
			api?.core.actions.execute(resetRemixThinkingPresentation);
		},
		[api, remixId],
	);

	useEditorRemixLifecycleMirror({
		animationFinished: presentation.animationFinished,
		presentationRemixId: presentation.remixId ?? null,
		dispatchProjection,
	});

	// Publish the phase alongside the projection this host already mirrors. Presentation consumers
	// need to tell `inserting` apart from a genuinely ready result, and the projected booleans are
	// identical for both. Written here because this host has the api, the session token, and the
	// lifecycle context in one place.
	const effectivePhase = terminalPhase ?? phase;
	const presentationToken = presentation.token;
	useLayoutEffect(() => {
		if (presentation.remixId !== remixId) {
			// Adjacent sessions during replacement: never label B's presentation with A's phase.
			return;
		}
		markEditorRemixLifecyclePhase(api, presentationToken, effectivePhase);
	}, [api, effectivePhase, presentationToken, presentation.remixId, remixId]);

	const commandHandlers = useMemo<EditorRemixCommandHandlers>(
		() => ({
			onReady: () => {
				// Lifecycle completion is projected by the mirror; this starts presentation only.
				getEditorRemixUxHandles(api)?.markReady();
			},
			onErrored: (command) => {
				const handles = getEditorRemixUxHandles(api);
				if (!handles) {
					return;
				}
				handles.settleExperience({
					kind: 'failed',
					// Match the legacy fallback when no Rovo store error is available.
					error: new Error(DEFAULT_REMIX_ERROR_MESSAGE),
				});
				// Stop before closing: stop preserves selection but reopens the modal.
				handles.stopThinking({ reopenModal: true });
				resetTerminalPresentation(command.remixId);
				handles.closeTerminalPreview();
				handles.closeModal();
				handles.openSidebar();
			},
			onAborted: (command) => {
				const handles = getEditorRemixUxHandles(api);
				if (!handles) {
					return;
				}
				const presentSourceOrTargetInvalidation = consumeEditorRemixMutationInvalidation(
					api,
					command.remixId,
				);
				// `RemixAborted` is origin-blind, so it settles with the reason legacy reports for an
				// abort this surface did not initiate, keeping the two cohorts comparable. Legacy
				// conflates the same cases under that wording, so matching it reproduces legacy
				// exactly, and the content component still overrides it with a recorded local intent.
				if (
					handles.settleExperience({
						kind: 'aborted',
						reason: REMIX_REMOTE_ABORT_REASON,
						remixId: command.remixId,
					}) === 'ignored'
				) {
					// A different terminal outcome already owns teardown.
					return;
				}
				if (!presentSourceOrTargetInvalidation) {
					handles.stopThinking({ reopenModal: false });
					resetTerminalPresentation(command.remixId);
					return;
				}
				// Source invalidation must preserve the current Rovo Chat sidebar state.
				handles.stopThinking({ reopenModal: true });
				resetTerminalPresentation(command.remixId);
				handles.closeTerminalPreview();
				handles.closeModal();
			},
			onInserted: (command) => {
				// The machine has accepted INSERTED only after the editor acknowledges the write.
				// Reset the matching decoration explicitly: lifecycle projection intentionally leaves
				// presentation untouched, and a stale command must never clear a replacement session.
				resetTerminalPresentation(command.remixId);
			},
			onCancellationSettled: () => {
				// Cancellation teardown already handled presentation cleanup.
				getEditorRemixUxHandles(api)?.settleCancellation();
			},
			onHandlerError: ({ commandType, error }) => {
				try {
					createAnalyticsEvent({
						eventType: 'operational',
						action: 'failed',
						actionSubject: 'editorRemixCommandHandler',
						actionSubjectId: commandType,
						source: 'editorRemix',
						attributes: {
							operation: 'commandEffect',
							commandType,
							// Do not log service-supplied error messages.
							errorName: error instanceof Error ? error.name : typeof error,
						},
					}).fire();
				} catch {
					// Diagnostics must never affect command handling.
				}
			},
		}),
		[api, createAnalyticsEvent, resetTerminalPresentation],
	);

	useEditorRemixCommands(commandHandlers);

	useLayoutEffect(() => {
		if (api?.malleableUi?.sharedState?.currentState()?.isAnyRemixing === isAnyRemixing) {
			return;
		}
		api?.core.actions.execute(setIsAnyRemixing(api, isAnyRemixing));
	}, [api, isAnyRemixing]);

	return null;
};
